import { useEffect, useRef, useState } from 'react'

/**
 * Cloudflare Turnstile, the bot check on the sign-in link request.
 *
 * Loaded from Cloudflare because Cloudflare does not support self-hosting the script (it changes with
 * the bot signals), which is why the proxy's Content-Security-Policy names https://challenges.cloudflare.com
 * for scripts and frames and nothing else new. It is only ever loaded when the API reports a site key
 * (`GET /api/auth/config`), so with the check off no request is made to Cloudflare at all.
 *
 * A TOKEN WORKS ONCE and lasts five minutes. So the page asks for a new one after every request
 * (`resetKey`), whether the request succeeded or not, and the "Resend link" button on the next screen has
 * a widget of its own. A token this component handed up is also withdrawn (`onToken(null)`) when the
 * widget goes away, so a screen that replaces it never starts from a token that may be about to expire.
 */

interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string
      theme?: 'auto' | 'light' | 'dark'
      callback?: (token: string) => void
      'expired-callback'?: () => void
      'timeout-callback'?: () => void
      'error-callback'?: () => void
    },
  ): string
  reset(widgetId?: string): void
  remove(widgetId?: string): void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

/**
 * How long to wait for Cloudflare's script. A filter that neither blocks the request nor lets it through
 * fires neither `load` nor `error`, which would leave the button disabled for ever with no word said.
 */
const LOAD_TIMEOUT_MS = 15_000

let loading: Promise<TurnstileApi> | null = null

/** Loads Cloudflare's script once for the whole page, and resolves with its API. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (loading) return loading

  loading = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_URL
    script.async = true
    script.defer = true

    // A failed attempt takes its tag with it, so a retry does not leave a second copy of the script on
    // the page.
    const fail = (reason: string) => {
      clearTimeout(timer)
      script.remove()
      reject(new Error(reason))
    }

    const timer = setTimeout(() => fail('turnstile did not load in time'), LOAD_TIMEOUT_MS)

    script.onload = () => {
      clearTimeout(timer)
      if (window.turnstile) resolve(window.turnstile)
      else fail('turnstile did not initialise')
    }
    script.onerror = () => fail('turnstile did not load')

    document.head.appendChild(script)
  }).catch((error: unknown) => {
    // Forget the failure, so a later mount (the person fixed their network) can try again.
    loading = null
    throw error
  })

  return loading
}

/**
 * Forgets that the script was ever loaded, so a test can start from a page that has not asked Cloudflare
 * for it. In use the cache is right: one script for the page's whole life.
 */
export function resetTurnstileLoaderForTests(): void {
  loading = null
}

export interface TurnstileProps {
  siteKey: string
  /** The token when the check is passed; null when it expires, times out, errors, is reset or goes away. */
  onToken: (token: string | null) => void
  /** Bump to discard the current token and ask the visitor to pass the check again. */
  resetKey?: number
}

export function Turnstile({ siteKey, onToken, resetKey = 0 }: TurnstileProps) {
  const container = useRef<HTMLDivElement>(null)
  const widget = useRef<{ id: string; api: TurnstileApi } | null>(null)
  const [failed, setFailed] = useState(false)

  // The latest callback, without re-rendering the widget each time the parent re-renders.
  const onTokenRef = useRef(onToken)
  onTokenRef.current = onToken

  useEffect(() => {
    let cancelled = false

    loadTurnstile()
      .then((api) => {
        if (cancelled || !container.current) return

        const id = api.render(container.current, {
          sitekey: siteKey,
          theme: 'auto',
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'timeout-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        })

        widget.current = { id, api }
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      if (widget.current) {
        widget.current.api.remove(widget.current.id)
        widget.current = null
      }
      // Whatever token this widget produced is no longer the page's to use: the next widget makes its
      // own, and a token held across the change may be minutes old.
      onTokenRef.current(null)
    }
  }, [siteKey])

  // Not on the first render: the widget is brand new then and has nothing to discard.
  const first = useRef(true)
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }

    onTokenRef.current(null)
    if (widget.current) widget.current.api.reset(widget.current.id)
  }, [resetKey])

  return (
    <div className="challenge-slot">
      <div ref={container} data-testid="turnstile-widget" />
      {failed ? (
        <p className="form-footnote" role="alert">
          The security check could not load. Check that your network, or a browser extension, is not
          blocking challenges.cloudflare.com, then reload this page.
        </p>
      ) : null}
    </div>
  )
}
